import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import instagramLogo from '../../assets/instagram.png';
import whatsappLogo from '../../assets/whatsappChart.png';
import facebookLogo from '../../assets/facebook.png';
import threadsLogo from '../../assets/Threads.png';
import { getCRMSourcesStats } from '../../api';
import { toTitleCase } from '@/common/utils';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';

const KNOWN_PLATFORMS = {
  whatsapp: { label: 'Whatsapp', color: '#50AB3D', logo: whatsappLogo },
  instagram: { label: 'Instagram', color: '#EEB90B', logo: instagramLogo },
  facebook: { label: 'Facebook', color: '#0088FF', logo: facebookLogo },
  thread: { label: 'Thread', color: '#FF383C', logo: threadsLogo },
};

const FALLBACK_COLORS = [
  '#FF610E',
  '#A855F7',
  '#14B8A6',
  '#F97316',
  '#0088FF',
  '#50AB3D',
];

function matchKnownPlatform(sourceType = '') {
  const key = String(sourceType).trim().toLowerCase();
  return Object.keys(KNOWN_PLATFORMS).find((id) => key.includes(id)) || null;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function getBarTotal(row, platforms) {
  return platforms.reduce((sum, p) => sum + (Number(row[p.id]) || 0), 0);
}

function normalizeLeadSources(data) {
  const sources = data?.sources || [];

  let fallbackIndex = 0;
  const platforms = sources.map((source) => {
    const knownId = matchKnownPlatform(source.source_type);
    const known = knownId ? KNOWN_PLATFORMS[knownId] : null;
    const color =
      known?.color || FALLBACK_COLORS[fallbackIndex++ % FALLBACK_COLORS.length];
    return {
      id: source.source_type,
      label:
        known?.label ||
        toTitleCase(String(source.source_type).replace(/_/g, ' ')),
      color,
      logo: known?.logo || null,
      count: Number(source.contact_count) || 0,
    };
  });

  const monthlyByName = new Map(
    (data?.monthly_data || []).map((row) => [row.name, row]),
  );
  const series = MONTHS.map((month) => {
    const apiRow = monthlyByName.get(month);
    const row = { month };
    platforms.forEach((p) => {
      row[p.id] = Number(apiRow?.[p.id]) || 0;
    });
    return row;
  });

  return { platforms, series };
}

function formatCount(n) {
  return String(Number(n) || 0).padStart(2, '0');
}

function niceNumber(value, round) {
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
}

function getTickSpacing(maxRaw, tickCount = 4) {
  const range = niceNumber(maxRaw, false);
  return Math.max(1, niceNumber(range / tickCount, true));
}

function niceMax(value, tickCount = 4) {
  if (value <= 0) return tickCount;
  const padded = value * 1.08;
  const spacing = getTickSpacing(padded, tickCount);
  return Math.ceil(padded / spacing) * spacing;
}

function buildTicks(max, tickCount = 4) {
  if (max <= 0) return [0, 1];
  const spacing = getTickSpacing(max, tickCount);
  const ticks = [];
  for (let tick = 0; tick <= max; tick += spacing) ticks.push(tick);
  return ticks;
}

function SourceSummaryCard({ platform }) {
  return (
    <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 rounded-[12px] bg-[#F5F5F5] px-2.5 py-2 sm:px-3 sm:py-2.5">
      <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center">
        {platform.logo ? (
          <img
            src={platform.logo}
            alt={`${platform.label} logo`}
            className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
          />
        ) : (
          <span
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-semibold shrink-0"
            style={{ backgroundColor: platform.color }}
          >
            {platform.label.charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 flex flex-col justify-center">
        <span
          className="text-[11px] sm:text-[12px] leading-tight font-medium text-[#868686] truncate"
          title={platform.label}
        >
          {platform.label}
        </span>
        <span className="text-[16px] sm:text-[18px] lg:text-[20px] leading-tight font-semibold text-[#1F2241] tabular-nums whitespace-nowrap">
          {typeof platform.count === 'number'
            ? platform.count.toLocaleString()
            : (platform.count ?? 0)}
        </span>
      </div>
    </div>
  );
}

const TOOLTIP_WIDTH = 240;

function StackedBarChart({ series, platforms }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const barRefs = useRef([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Position relative to wrapperRef (which doesn't scroll), clamped to stay
  // fully on-screen — the bars live inside a horizontally-scrolling track,
  // so a bar near the scrolled-off edge would otherwise center a tooltip
  // partway outside the visible viewport and get clipped by overflow-x-auto.
  const [tooltipLeft, setTooltipLeft] = useState(0);

  const { maxValue, ticks } = useMemo(() => {
    const maxRaw = Math.max(
      1,
      ...series.map((row) => getBarTotal(row, platforms)),
    );
    const max = niceMax(maxRaw);
    return { maxValue: max, ticks: buildTicks(max) };
  }, [series, platforms]);

  const active = activeIndex != null ? series[activeIndex] : null;

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;

    const updateScrollIndicators = () => {
      const maxLeft = node.scrollWidth - node.clientWidth;
      setCanScrollLeft(node.scrollLeft > 2);
      setCanScrollRight(node.scrollLeft < maxLeft - 2);
    };

    const updateTooltipPosition = () => {
      const bar = barRefs.current[activeIndex];
      const wrapper = wrapperRef.current;
      if (!bar || !wrapper) return;
      const barRect = bar.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const barCenter = barRect.left + barRect.width / 2 - wrapperRect.left;
      const half = TOOLTIP_WIDTH / 2;
      const clamped = Math.min(
        Math.max(barCenter, half + 8),
        wrapperRect.width - half - 8,
      );
      setTooltipLeft(clamped);
    };

    updateScrollIndicators();
    if (activeIndex != null) updateTooltipPosition();
    node.addEventListener('scroll', updateScrollIndicators, { passive: true });
    node.addEventListener('scroll', updateTooltipPosition, { passive: true });
    window.addEventListener('resize', updateScrollIndicators);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      node.removeEventListener('scroll', updateScrollIndicators);
      node.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateScrollIndicators);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [activeIndex]);

  const nudgeHorizontalScroll = (direction) => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollBy({
      left: direction === 'left' ? -220 : 220,
      behavior: 'smooth',
    });
  };

  return (
    <div ref={wrapperRef} className="relative w-full h-full pt-2 flex flex-col">
      {canScrollLeft && (
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-20 w-10 bg-gradient-to-r from-white to-transparent" />
      )}
      {canScrollRight && (
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-20 w-10 bg-gradient-to-l from-white to-transparent" />
      )}

      {canScrollLeft && (
        <button
          type="button"
          onClick={() => nudgeHorizontalScroll('left')}
          className="absolute left-1 top-1/2 -translate-y-1/2 z-30 h-7 w-7 rounded-full border border-[#EBF4EA] bg-white/95 text-[#868686] shadow-sm"
          aria-label="Scroll chart left"
        >
          <span className="text-sm leading-none">‹</span>
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          onClick={() => nudgeHorizontalScroll('right')}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-30 h-7 w-7 rounded-full border border-[#EBF4EA] bg-white/95 text-[#868686] shadow-sm"
          aria-label="Scroll chart right"
        >
          <span className="text-sm leading-none">›</span>
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex-1 h-full overflow-x-auto overflow-y-visible no-scrollbar"
      >
        <div className="min-w-[620px] h-full flex flex-col pb-2">
          <div className="flex h-full gap-2 sm:gap-3 pt-3">
            {/* Y axis */}
            <div className="relative w-10 sm:w-12 shrink-0 h-full min-h-[220px] sm:min-h-[260px]">
              {ticks
                .slice()
                .reverse()
                .map((tick) => {
                  const top = ((maxValue - tick) / maxValue) * 100;
                  return (
                    <span
                      key={tick}
                      className="absolute right-0 -translate-y-1/2 text-[11px] sm:text-[12px] font-medium text-[#868686] tabular-nums"
                      style={{ top: `${top}%` }}
                    >
                      {tick}
                    </span>
                  );
                })}
            </div>

            {/* Plot */}
            <div className="relative flex-1 min-w-0 h-full flex flex-col">
              <div className="relative flex-1 min-h-[220px] sm:min-h-[260px]">
                {/* Grid lines */}
                {ticks.map((tick) => {
                  const top = ((maxValue - tick) / maxValue) * 100;
                  return (
                    <div
                      key={tick}
                      className="absolute left-0 right-0 border-t border-dashed border-[#E5E5E5]"
                      style={{ top: `${top}%` }}
                    />
                  );
                })}

                {/* Bars — slim columns with generous gaps (Figma balance) */}
                <div className="absolute inset-0 flex items-end justify-between px-2 sm:px-3">
                  {series.map((row, index) => {
                    const segments = platforms.map((p) => ({
                      id: p.id,
                      color: p.color,
                      value: Number(row[p.id]) || 0,
                    }));
                    const total = segments.reduce((sum, s) => sum + s.value, 0);
                    const heightPct = Math.max(0, (total / maxValue) * 100);

                    return (
                      <button
                        key={row.month}
                        ref={(el) => {
                          barRefs.current[index] = el;
                        }}
                        type="button"
                        className="relative flex-1 h-full flex flex-col items-center justify-end focus:outline-none"
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseLeave={() => setActiveIndex(null)}
                        onFocus={() => setActiveIndex(index)}
                        onBlur={() => setActiveIndex(null)}
                        aria-label={`${row.month} lead sources`}
                      >
                        <div
                          className="w-[12px] sm:w-[16px] lg:w-[18px] xl:w-[22px] overflow-hidden rounded-t-full transition-opacity duration-200"
                          style={{
                            height: `${heightPct}%`,
                            opacity:
                              activeIndex == null || activeIndex === index
                                ? 1
                                : 0.45,
                          }}
                        >
                          <div className="flex flex-col-reverse w-full h-full">
                            {total > 0 &&
                              segments.map((seg) => {
                                if (seg.value <= 0) return null;
                                const segPct = (seg.value / total) * 100;
                                const topSegId = [...segments]
                                  .reverse()
                                  .find((s) => s.value > 0)?.id;
                                const isTop = seg.id === topSegId;
                                return (
                                  <div
                                    key={seg.id}
                                    className={`w-full ${isTop ? 'rounded-t-full' : ''}`}
                                    style={{
                                      height: `${segPct}%`,
                                      backgroundColor: seg.color,
                                    }}
                                  />
                                );
                              })}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* X axis */}
              <div className="mt-2 flex justify-between px-2 sm:px-3 pb-1 shrink-0">
                {series.map((row) => (
                  <span
                    key={row.month}
                    className="flex-1 text-center text-[10px] sm:text-[12px] font-medium tracking-tight text-[#868686]"
                  >
                    {row.month}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip — sibling of the scrolling track (not inside it) so it's
          never clipped by overflow-x-auto; position is pre-clamped to the
          visible viewport (see updateTooltipPosition above). */}
      {active && activeIndex != null && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            left: tooltipLeft,
            top: 36,
            width: TOOLTIP_WIDTH,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="rounded-[12px] bg-white border border-[#EBF4EA] shadow-[0_8px_24px_rgba(2,7,2,0.12)] p-3.5">
            <p className="text-[13px] font-semibold text-[#1F2241]">
              Platform view distribution
            </p>
            <p className="text-[12px] font-medium text-[#868686] mt-0.5 mb-3">
              {active.month}
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {platforms.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span
                      className="w-0.5 h-3.5 rounded-full shrink-0"
                      style={{ backgroundColor: p.color }}
                    />
                    <span className="text-[12px] font-medium text-[#868686] truncate">
                      {p.label}
                    </span>
                  </div>
                  <span className="text-[12px] font-semibold text-[#1F2241] tabular-nums">
                    {formatCount(active[p.id])}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getCompanyId() {
  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const userObj = JSON.parse(storedUser);
      return userObj?.company_id || localStorage.getItem('company_id');
    }
  } catch (e) {
    console.error('Failed to parse user object from localStorage', e);
  }
  return localStorage.getItem('company_id');
}

export function SocialTrafficTable() {
  const companyId = getCompanyId();

  const {
    data: rawData,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['crm-dashboard-sources', companyId],
    queryFn: () => getCRMSourcesStats(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && rawData !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isError) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to load lead sources from the server.',
      );
    }
  }, [isError, error]);

  const { platforms, series } = useMemo(
    () => normalizeLeadSources(rawData),
    [rawData],
  );

  if (showSkeleton || !rawData) {
    return (
      <div className="bg-white rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-4 sm:p-6 h-full flex flex-col font-sf-pro-display animate-pulse">
        <div className="h-5 w-32 bg-skeleton-base rounded mb-5" />
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-[68px] bg-skeleton-base rounded-[12px]" />
          ))}
        </div>
        <div className="flex-1 min-h-[220px] bg-skeleton-base rounded-xl" />
      </div>
    );
  }

  const hasAnySource = platforms.length > 0;

  return (
    <div className="bg-white rounded-[20px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-4 sm:p-6 h-full flex flex-col font-sf-pro-display">
      <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241] mb-5">
        Lead Sources
      </h3>

      {!hasAnySource ? (
        <div className="flex-1 flex items-center justify-center text-text-secondary text-sm py-10">
          No lead source data available
        </div>
      ) : (
        <>
          <div
            className={`grid gap-2.5 sm:gap-3 mb-5 ${
              platforms.length === 1
                ? 'grid-cols-1 sm:grid-cols-2'
                : platforms.length === 2
                  ? 'grid-cols-2'
                  : platforms.length === 3
                    ? 'grid-cols-2 sm:grid-cols-3'
                    : 'grid-cols-2 sm:grid-cols-2 2xl:grid-cols-4'
            }`}
          >
            {platforms.map((platform) => (
              <SourceSummaryCard key={platform.id} platform={platform} />
            ))}
          </div>

          <div className="flex-1 min-h-0">
            <StackedBarChart series={series} platforms={platforms} />
          </div>
        </>
      )}
    </div>
  );
}
